import { Button, Form, Input, message } from "antd";
import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { loginAdmin } from "../../api";
import TwoFactorChallenge from "./TwoFactorChallenge";
import TrustBrowserPrompt from "./TrustBrowserPrompt";
import { trustedBrowserTokens } from "modules/auth/utils/trustedBrowsers";
import { useTranslation } from "react-i18next";
import SharedPhoneInput from "components/shared/PhoneNumInpt";
import { getSubdomain } from "utils";
import { Lock } from "components/shared/icons";
import CustomPage from "components/shared/custom-page";
import LoginBrandLogo from "./LoginBrandLogo";
import { identifyPostHogUser } from "posthog";
import {
  LOGIN_REDIRECT_PARAM,
  resolvePostLoginPath,
} from "modules/auth/utils/loginRedirect";

const LoginForm = () => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const lng = localStorage.getItem("default-lg");
  const isDark = +localStorage.getItem("isDark");
  const [termsOpen, setTermsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /**
   * The pending two-factor challenge, when the account has one. Holding it here is what
   * swaps the credentials form for the code step — the password has already been accepted
   * at that point, so there is nothing to go back to except starting over.
   */
  const [challenge, setChallenge] = useState(null);
  // Set after a passed second factor when the API offers to trust this browser.
  const [trustOffer, setTrustOffer] = useState(null);
  const subdomain = getSubdomain();

  const storeSession = (session) => {
    localStorage.setItem("token", session.access_token);
    localStorage.setItem("user", JSON.stringify(session));
    if (session.central_access_token) {
      localStorage.setItem(
        "central_access_token",
        session.central_access_token
      );
    }
    identifyPostHogUser(session);
  };

  const enterApp = (successMessage) => {
    message.success(successMessage || t("login.login_success"));
    // A full load, because App decides signed-in from storage at render. `replace`, so Back
    // from the page they were sent to does not reopen this form.
    window.location.replace(
      resolvePostLoginPath(
        searchParams.get(LOGIN_REDIRECT_PARAM),
        `/${lng}/home`
      )
    );
  };

  const completeLogin = (session) => {
    storeSession(session);
    enterApp();
  };

  // The session is stored before the trust step because trusting is an authenticated call.
  const completeVerifiedLogin = ({
    trusted_browser_offer: offer,
    ...session
  }) => {
    storeSession(session);

    if (!offer) {
      enterApp();
      return;
    }

    setTrustOffer({ offer, userId: session.id });
  };

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      const response = await loginAdmin({
        ...values,
        slug: subdomain,
        trusted_browser_tokens: trustedBrowserTokens(),
      });

      // customFetch resolves to undefined when the request never completed, and only the
      // failures it recognises as network ones reach the error boundary — so without this a
      // submit would silently do nothing and read as a dead button.
      if (!response) {
        message.error(t("formValidationMessages.serverError"));
        return;
      }

      if (!response.status) return;

      // The password was right but the account carries a second factor: no token yet, just a
      // handle to answer the challenge with.
      if (response.data?.two_factor_required) {
        setChallenge(response.data);
        return;
      }

      completeLogin(response.data);
    } finally {
      setSubmitting(false);
    }
  };
  const handleModeClick = () => {
    localStorage.setItem("isDark", isDark ? 0 : 1);
    window.location.reload();
  };

  const changeLng = () => {
    const newLanguage = i18n.language === "ar" ? "en" : "ar";
    localStorage.setItem("default-lg", newLanguage);
    i18n.changeLanguage(newLanguage);
    document.documentElement.setAttribute("lang", newLanguage);
    document.documentElement.setAttribute(
      "dir",
      newLanguage === "ar" ? "rtl" : "ltr"
    );
    window.location.reload();
  };

  return (
    <>
      <section className="flex flex-col lg:flex-row items-stretch relative min-h-screen h-auto lg:h-screen">
        <div className="absolute top-[32px] z-[60] end-[16px] lg:end-[calc(48%+16px)] inline-flex items-center gap-2">
          <div
            className={`
              w-9 h-9 p-1 rounded-[20px] border
              inline-flex justify-center items-center cursor-pointer
              ${isDark ? "bg-slate-900 border-slate-800" : "bg-surface border-border-light"}
            `}
            onClick={handleModeClick}
          >
            <img
              src={isDark ? "/icons/sun.svg" : "/icons/moon.svg"}
              alt="toggle mode"
            />
          </div>
          <div
            className={`
              w-9 h-9 p-1 rounded-[20px] border
              inline-flex justify-center items-center cursor-pointer
              ${isDark ? "bg-slate-900 border-slate-800" : "bg-surface border-border-light"}
            `}
            onClick={changeLng}
            title={i18n.language === "ar" ? "English" : "عربي"}
          >
            <img
              src={
                isDark ? "/icons/mlti-lang-dark.svg" : "/icons/mlti-lang.svg"
              }
              alt="toggle language"
              className="w-5 h-5"
            />
          </div>
        </div>

        <LoginBrandLogo isDark={isDark} />
        <div
          className={`w-full lg:w-[52%] flex flex-col justify-center px-4 sm:px-8 lg:px-[72px] py-8 lg:py-0 lg:h-full pb-[70px] ${
            isDark ? "bg-chrome" : "bg-surface"
          }`}
        >
          {!trustOffer && (
            <>
              <h1
                className={`text-xl sm:text-2xl lg:text-[24px] font-bold mt-6 mb-2.5 leading-6 sm:leading-8 ${
                  isDark ? "login-text-color" : "text-brand"
                }`}
              >
                {t("login.title")}
              </h1>

              <span
                className={`text-sm inline-block ${
                  isDark ? "text-[#B4B4B4]" : "text-[#737373]"
                }`}
              >
                {t("login.subTitle")}
              </span>
            </>
          )}

          {trustOffer ? (
            <TrustBrowserPrompt
              offer={trustOffer.offer}
              userId={trustOffer.userId}
              isDark={isDark}
              onDone={enterApp}
            />
          ) : challenge ? (
            <TwoFactorChallenge
              challenge={challenge}
              slug={subdomain}
              isDark={isDark}
              onVerified={completeVerifiedLogin}
              onCancel={() => setChallenge(null)}
            />
          ) : (
            <Form
              form={form}
              onFinish={onFinish}
              className={`login-form mt-8 ${isDark ? "login-form-dark" : ""}`}
            >
              <SharedPhoneInput name="mobile" form={form} />
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: t("login.login_password_err") },
                  {
                    min: 8,
                    max: 16,
                    message: t("formItemRules.lengthMessage", {
                      min: 8,
                      max: 16,
                    }),
                  },
                ]}
              >
                <Input.Password
                  placeholder={t("login.login_password_holder")}
                  prefix={<Lock isDark={isDark} />}
                  iconRender={(visible) =>
                    visible ? (
                      <EyeTwoTone width={20} />
                    ) : (
                      <EyeInvisibleOutlined width={20} />
                    )
                  }
                ></Input.Password>
              </Form.Item>
              <Link
                to={`/forget-password`}
                className={`text-sm relative bottom-1 inline-block ${
                  isDark ? "text-[#B4B4B4]" : "text-[#737373]"
                }`}
              >
                {t("login.forget_password")}
              </Link>
              <Button
                className="bg-brand rounded-lg flex items-center justify-center w-full h-[48px] sm:h-[56px] text-surface font-semibold mt-8 text-sm sm:text-base relative bottom-1"
                type="primary"
                htmlType="submit"
                loading={submitting}
              >
                {t("login.title")}
              </Button>
              <div className="self-stretch h-4 text-center justify-center mt-8">
                <span className="text-text-subtle text-xs font-normal">
                  {t("login.cnt")}
                </span>
                <span
                  className="text-text-subtle text-xs font-bold underline cursor-pointer"
                  onClick={() => setTermsOpen(true)}
                >
                  {t("login.terms")}
                </span>
                <span className="text-text-subtle text-xs font-normal">
                  {" "}
                  {t("login.us")}
                </span>
              </div>
            </Form>
          )}
        </div>

        <div
          className="relative w-full lg:w-[48%] min-h-[400px] lg:h-full bg-no-repeat bg-center order-first lg:order-last login-bg-image"
          style={{
            backgroundImage: "url('/login-img.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="lg:hidden absolute inset-0 z-50 flex items-center justify-center"></div>
        </div>
        <CustomPage
          open={termsOpen}
          onClose={() => setTermsOpen(false)}
          slug="terms-conditions-b2b"
        />
      </section>
    </>
  );
};

export default LoginForm;
